import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import QuestionnaireModal from "./QuestionnaireModal";

interface QuestionnaireTabProps {
  memberId: string;
  refreshMembers: () => void;
}

interface Questionnaire {
  id: number;
  title: string;
  status: "pending" | "completed";
}

interface QuestionnaireResponse {
  response_id: string;
  response_date: string;
  activity_id: string | null;
}

const QuestionnaireTab: React.FC<QuestionnaireTabProps> = ({
  memberId,
  refreshMembers,
}) => {
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<
    number | null
  >(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [responses, setResponses] = useState<{
    [key: number]: QuestionnaireResponse[];
  }>({});
  const [selectedResponse, setSelectedResponse] = useState<string | null>(null);
  const [isViewOnly, setIsViewOnly] = useState(false);

  useEffect(() => {
    fetchQuestionnaires();
  }, [memberId]);

  const fetchQuestionnaires = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/members/${memberId}/questionnaires`
      );
      setQuestionnaires(response.data);
      fetchAllResponses(response.data);
    } catch (error) {
      console.error("Error fetching questionnaires:", error);
    }
  };

  const fetchAllResponses = async (questionnaires: Questionnaire[]) => {
    const allResponses: { [key: number]: QuestionnaireResponse[] } = {};
    for (const questionnaire of questionnaires) {
      if (questionnaire.status === "completed") {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/members/${memberId}/questionnaires/${questionnaire.id}/responses`
          );
          allResponses[questionnaire.id] = response.data;
        } catch (error) {
          console.error(
            `Error fetching responses for questionnaire ${questionnaire.id}:`,
            error
          );
        }
      }
    }
    setResponses(allResponses);
  };

  const handleQuestionnaireClick = (questionnaireId: number) => {
    setSelectedQuestionnaire(questionnaireId);
    setIsModalOpen(true);
    setSelectedResponse(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedQuestionnaire(null);
    setSelectedResponse(null);
    fetchQuestionnaires();
    setIsViewOnly(false);
  };

  const handleViewResponse = (questionnaireId: number, responseId: string) => {
    setSelectedQuestionnaire(questionnaireId);
    setSelectedResponse(responseId);
    setIsViewOnly(true);
    setIsModalOpen(true);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Questionnaires
      </Typography>
      <List>
        {questionnaires.map((questionnaire) => (
          <Accordion key={questionnaire.id}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <ListItemText
                primary={questionnaire.title}
                secondary={
                  questionnaire.status === "completed" ? "Completed" : "Pending"
                }
              />
              <Chip
                label={
                  questionnaire.status === "completed" ? "Completed" : "Pending"
                }
                color={
                  questionnaire.status === "completed" ? "success" : "warning"
                }
                sx={{ marginRight: 1 }}
              />
            </AccordionSummary>
            <AccordionDetails>
              {questionnaire.status === "completed" ? (
                responses[questionnaire.id]?.map((response) => (
                  <Box key={response.response_id} sx={{ mt: 1 }}>
                    <Button
                      onClick={() =>
                        handleViewResponse(
                          questionnaire.id,
                          response.response_id
                        )
                      }
                      variant="outlined"
                      sx={{ mr: 1 }}
                    >
                      View Response (
                      {new Date(response.response_date).toLocaleDateString()})
                    </Button>
                    {response.activity_id && (
                      <Chip
                        label={`Associated with Activity #${response.activity_id}`}
                        color="info"
                        size="small"
                      />
                    )}
                  </Box>
                ))
              ) : (
                <Button
                  variant="contained"
                  onClick={() => handleQuestionnaireClick(questionnaire.id)}
                  color="primary"
                >
                  Fill Out
                </Button>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </List>
      {selectedQuestionnaire && (
        <QuestionnaireModal
          open={isModalOpen}
          onClose={handleCloseModal}
          memberId={memberId}
          questionnaireId={selectedQuestionnaire}
          responseId={selectedResponse}
          refreshMembers={refreshMembers}
          isViewOnly={isViewOnly}
        />
      )}
    </Box>
  );
};

export default QuestionnaireTab;
